import React from "react";
import { Card } from "../components/ui/card";
import { Users, Code, Headphones, Crown, Sparkles, Mail, Linkedin, User, Target, Lightbulb, Star, Zap, Heart, Award, Globe } from "lucide-react";
import { motion } from "framer-motion";

export default function Team() {
  const team = [
    {
      name: "Samuel Desiano",
      role: "Founder, CEO & Manager",
      description: "Visionary leader driving Zenith's mission to transform education through innovative peer-to-peer learning solutions.",
      linkedin: "https://www.linkedin.com/in/samuel-desiano/",
      icon: Crown,
      gradient: "from-blue-500 via-blue-600 to-purple-600 dark:from-red-500 dark:via-orange-500 dark:to-orange-600",
      iconBg: "from-blue-100 to-purple-100 dark:from-red-900/50 dark:to-orange-900/50",
      accentColor: "text-blue-500 dark:text-orange-400",
      hasLinkedin: true
    },
    {
      name: "Avanish Pakalapati",
      role: "Lead Developer",
      description: "Architect of Zenith's technical infrastructure, crafting seamless learning experiences with cutting-edge technology.",
      email: "avanishpakalapati@gmail.com",
      linkedin: "https://www.linkedin.com/in/avanish-pakalapati/",
      icon: Code,
      gradient: "from-fuchsia-500 via-pink-500 to-rose-600 dark:from-violet-600 dark:via-purple-600 dark:to-fuchsia-600", // NEW COLOR
      iconBg: "from-fuchsia-100 to-rose-100 dark:from-violet-900/50 dark:to-fuchsia-900/50", // NEW COLOR
      accentColor: "text-fuchsia-500 dark:text-purple-400", // NEW COLOR
      hasEmail: true,
      hasLinkedin: true
    },
    {
      name: "Luca Bonagura",
      role: "Manager",
      description: "Strategic operations expert ensuring seamless experiences for students and tutors, while building strong community connections.",
      icon: Headphones,
      gradient: "from-green-500 via-emerald-500 to-teal-600 dark:from-emerald-600 dark:via-teal-600 dark:to-cyan-600",
      iconBg: "from-green-100 to-teal-100 dark:from-emerald-900/50 dark:to-cyan-900/50",
      accentColor: "text-green-500 dark:text-cyan-400",
      hasGlobe: true
    }
  ];

  return (
    <div className="min-h-screen py-12 md:py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 relative overflow-hidden transition-colors duration-300">
      {/* Enhanced Background decoration */}
      <div className="absolute inset-0 opacity-20 dark:opacity-20">
        <motion.div
          animate={{ 
            x: [-100, 100, -100],
            y: [-50, 50, -50] 
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-20 left-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-300/40 to-purple-300/40 dark:from-red-600/20 dark:to-orange-600/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            x: [100, -100, 100],
            y: [50, -50, 50] 
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-20 right-0 w-[550px] h-[550px] bg-gradient-to-br from-purple-300/40 to-pink-300/40 dark:from-orange-500/20 dark:to-amber-500/20 rounded-full blur-3xl"
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-pink-200/30 to-blue-200/30 dark:from-amber-500/10 dark:to-red-500/10 rounded-full blur-3xl" />
      </div>

      {/* Floating decorative shapes */}
      <motion.div
        animate={{ y: [0, -40, 0], rotate: [0, 15, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-40 right-20 hidden lg:block"
      >
        <div className="w-28 h-28 bg-gradient-to-br from-yellow-300/50 to-orange-300/50 dark:from-amber-500/30 dark:to-orange-500/30 rounded-3xl opacity-60 shadow-2xl backdrop-blur-sm" />
      </motion.div>

      <motion.div
        animate={{ y: [0, 40, 0], rotate: [0, -15, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-40 left-20 hidden lg:block"
      >
        <div className="w-36 h-36 bg-gradient-to-br from-blue-300/50 to-purple-400/50 dark:from-red-500/30 dark:to-orange-500/30 rounded-full opacity-60 shadow-2xl backdrop-blur-sm" />
      </motion.div>

      <motion.div
        animate={{ scale: [1, 1.3, 1], rotate: [0, 180, 360] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
        className="absolute top-1/3 left-10 hidden xl:block"
      >
        <Star className="w-20 h-20 text-pink-400/40 dark:text-amber-400/40 fill-pink-400/20 dark:fill-amber-400/20" />
      </motion.div>

      <motion.div
        animate={{ scale: [1, 1.4, 1], rotate: [0, -180, -360] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        className="absolute top-2/3 right-10 hidden xl:block"
      >
        <Sparkles className="w-24 h-24 text-blue-400/40 dark:text-orange-400/40" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div 
          className="text-center mb-24"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        > 
          <motion.h1 
            className="text-6xl md:text-8xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:bg-gradient-to-r dark:from-red-400 dark:via-orange-400 dark:to-amber-400 bg-clip-text text-transparent mb-8 pb-2 drop-shadow-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            The People Behind Zenith
          </motion.h1>
          
          <motion.p 
            className="text-2xl md:text-3xl text-gray-700 dark:text-zinc-300 max-w-4xl mx-auto leading-relaxed font-medium mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            A passionate team dedicated to transforming education through{" "}
            <span className="font-black text-purple-600 dark:text-orange-400 whitespace-nowrap relative">
              peer-to-peer learning
              <motion.div 
                className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-pink-400 dark:from-orange-400 dark:to-amber-400 rounded-full"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              />
            </span>
          </motion.p>

          {/* Animated dots separator */}
          <motion.div 
            className="flex justify-center gap-3 mt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                animate={{ 
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3]
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  delay: i * 0.3
                }}
                className="w-3 h-3 bg-blue-500 dark:bg-orange-400 rounded-full"
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Our Team Section - All members side by side */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-28 relative"
        >
          {/* Decorative elements */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute -top-12 right-1/4 hidden lg:block"
          >
            <Zap className="w-16 h-16 text-blue-400/30 dark:text-orange-400/30" />
          </motion.div>

          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-4 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-orange-900/20 dark:to-amber-900/20 px-8 py-4 rounded-3xl shadow-2xl backdrop-blur-sm border border-blue-200 dark:border-orange-500/20">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-10 h-10 text-blue-500 dark:text-orange-500" />
              </motion.div>
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 dark:text-white">
                Our Team
              </h2>
              <motion.div
                animate={{ rotate: [0, -360] }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-10 h-10 text-blue-500 dark:text-orange-500" />
              </motion.div>
            </div>
            <p className="text-xl text-gray-700 dark:text-zinc-400 font-medium tracking-wide max-w-3xl mx-auto">
              The dedicated professionals behind Zenith Academics
            </p>
          </div>

          {/* Team Members Grid - All side by side */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + index * 0.2, duration: 0.6 }}
              >
                <Card className="p-8 border-4 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl shadow-3xl relative overflow-hidden group hover:shadow-4xl transition-all duration-500 h-full border-purple-300/50 hover:border-pink-400/70 dark:border-orange-500/30 dark:hover:border-amber-500/50">
                  {/* Animated background gradient */}
                  <motion.div
                    animate={{ 
                      x: [-100, 100, -100],
                      opacity: [0.05, 0.15, 0.05]
                    }}
                    transition={{ duration: 6, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent ${member.accentColor}`}
                  />
                  
                  {/* Floating icons */}
                  <motion.div
                    animate={{ y: [0, -20, 0], opacity: [0.3, 0.8, 0.3] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute top-6 left-6"
                  >
                    <Heart className="w-8 h-8 text-pink-400 dark:text-orange-400" />
                  </motion.div>
                  
                  <motion.div
                    animate={{ y: [0, 20, 0], opacity: [0.3, 0.8, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                    className="absolute bottom-6 right-6"
                  >
                    <Star className="w-8 h-8 text-blue-400 dark:text-amber-400" />
                  </motion.div>

                  <div className="relative h-full flex flex-col">
                    {/* Icon Avatar */}
                    <div className="relative mb-8">
                      <motion.div 
                        className={`w-40 h-40 mx-auto rounded-3xl bg-gradient-to-br ${member.gradient} flex items-center justify-center shadow-3xl group-hover:shadow-4xl transition-all duration-500 relative overflow-hidden`}
                        whileHover={{ scale: 1.05, rotate: index % 2 === 0 ? 3 : -3 }}
                        animate={{ 
                          boxShadow: [
                            "0 20px 60px rgba(0,0,0,0.2)",
                            "0 30px 80px rgba(0,0,0,0.3)",
                            "0 20px 60px rgba(0,0,0,0.2)"
                          ]
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <member.icon className="w-20 h-20 text-white" strokeWidth={2} />
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                      </motion.div>
                      
                      {/* Outer glow */}
                      <motion.div 
                        animate={{ 
                          scale: [1, 1.15, 1],
                          opacity: [0.3, 0.5, 0.3]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={`absolute inset-0 mx-auto w-40 h-40 bg-gradient-to-br ${member.gradient} rounded-3xl blur-3xl`}
                      />
                      
                      {/* Badge */}
                      <motion.div 
                        className="absolute -top-3 -right-3 w-14 h-14 bg-gradient-to-br from-blue-400 to-purple-500 dark:from-orange-500 dark:to-amber-500 rounded-full flex items-center justify-center shadow-2xl z-20"
                        animate={{ 
                          scale: [1, 1.2, 1],
                          rotate: [0, 180, 360]
                        }}
                        transition={{ duration: 4, repeat: Infinity }}
                      >
                        {index === 0 ? (
                          <Crown className="w-6 h-6 text-white" />
                        ) : (
                          <Award className="w-6 h-6 text-white" />
                        )}
                      </motion.div>
                    </div>

                    {/* Info */}
                    <div className="text-center space-y-4 flex-1">
                      <h3 className="text-3xl font-black text-gray-900 dark:text-white">
                        {member.name}
                      </h3>
                      <div className={`inline-block px-6 py-3 rounded-2xl bg-gradient-to-r ${member.gradient} text-white font-bold text-base shadow-2xl relative overflow-hidden group-hover:shadow-3xl transition-all duration-300`}>
                        {member.role}
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                      </div>
                      <p className="text-gray-700 dark:text-zinc-300 leading-relaxed text-lg font-medium">
                        {member.description}
                      </p>
                    </div>

                    {/* Decorative separator */}
                    <div className="flex justify-center my-8">
                      <div className="w-24 h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent dark:via-orange-400 rounded-full" />
                    </div>

                    {/* Contact Links - Different for each team member */}
                    <div className="mt-auto">
                      <div className="flex justify-center gap-6">
                        {/* Samuel: LinkedIn only */}
                        {member.hasLinkedin && !member.hasEmail && (
                          <motion.a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`w-14 h-14 bg-gradient-to-br ${member.iconBg} rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 backdrop-blur-sm border border-purple-200 dark:border-orange-500/30 relative overflow-hidden group`}
                            whileHover={{ scale: 1.1, y: -2, rotate: -5 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Linkedin className="w-6 h-6 text-purple-600 dark:text-orange-400 z-10" />
                            <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                          </motion.a>
                        )}
                        
                        {/* Avanish: Both Email and LinkedIn */}
                        {member.hasEmail && member.hasLinkedin && (
                          <>
                            <motion.a
                              href={`mailto:${member.email}`}
                              className={`w-14 h-14 bg-gradient-to-br ${member.iconBg} rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 backdrop-blur-sm border border-purple-200 dark:border-orange-500/30 relative overflow-hidden group`}
                              whileHover={{ scale: 1.1, y: -2, rotate: 5 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Mail className="w-6 h-6 text-purple-600 dark:text-orange-400 z-10" />
                              <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                            </motion.a>
                            <motion.a
                              href={member.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`w-14 h-14 bg-gradient-to-br ${member.iconBg} rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 backdrop-blur-sm border border-purple-200 dark:border-orange-500/30 relative overflow-hidden group`}
                              whileHover={{ scale: 1.1, y: -2, rotate: -5 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Linkedin className="w-6 h-6 text-purple-600 dark:text-orange-400 z-10" />
                              <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                            </motion.a>
                          </>
                        )}
                        
                        {/* Luca: Globe icon */}
                        {member.hasGlobe && (
                          <motion.div
                            className={`w-14 h-14 bg-gradient-to-br ${member.iconBg} rounded-2xl flex items-center justify-center shadow-xl backdrop-blur-sm border border-purple-200 dark:border-orange-500/30 relative overflow-hidden group`}
                            whileHover={{ scale: 1.1, y: -2, rotate: 5 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Globe className="w-6 h-6 text-purple-600 dark:text-orange-400 z-10" />
                            <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Our Vision Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-20"
        >
          <Card className="p-12 md:p-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 dark:bg-gradient-to-br dark:from-red-600 dark:via-orange-600 dark:to-amber-600 border-none shadow-3xl text-white relative overflow-hidden group">
            {/* Animated background particles */}
            <div className="absolute inset-0 opacity-10">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ 
                    y: [0, -100, 0],
                    x: [0, Math.sin(i) * 50, 0],
                    opacity: [0, 1, 0]
                  }}
                  transition={{ 
                    duration: 3 + Math.random() * 4,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                  className="absolute w-2 h-2 bg-white rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`
                  }}
                />
              ))}
            </div>

            {/* Floating decorative elements */}
            <motion.div
              animate={{ y: [0, -30, 0], rotate: [0, 360, 0] }}
              transition={{ duration: 12, repeat: Infinity }}
              className="absolute top-10 right-10"
            >
              <Star className="w-16 h-16 text-yellow-300/40 dark:text-amber-300/40 fill-yellow-300/20 dark:fill-amber-300/20" />
            </motion.div>

            <motion.div
              animate={{ y: [0, 30, 0], x: [0, 15, 0] }}
              transition={{ duration: 10, repeat: Infinity }}
              className="absolute bottom-10 left-10"
            >
              <Sparkles className="w-14 h-14 text-pink-300/40 dark:text-orange-300/40" />
            </motion.div>

            <div className="relative">
              <div className="flex items-center justify-center gap-4 mb-10 flex-wrap">
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, 0]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <Lightbulb className="w-14 h-14 text-yellow-300 dark:text-amber-300" />
                </motion.div>
                <h3 className="text-5xl md:text-6xl font-black drop-shadow-2xl text-center">
                  Our Vision
                </h3>
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, -10, 0]
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                >
                  <Target className="w-14 h-14 text-yellow-300 dark:text-amber-300" />
                </motion.div>
              </div>
              <div className="space-y-8 max-w-5xl mx-auto">
                <p className="text-2xl md:text-3xl text-white leading-relaxed drop-shadow-lg text-center">
                  At Zenith Academics, we believe that{" "}
                  <span className="font-black text-yellow-300 dark:text-amber-200 relative">
                    every student deserves access to quality education
                    <motion.div
                      animate={{ scaleX: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute -bottom-2 left-0 w-full h-1 bg-yellow-300 dark:bg-amber-300 rounded-full"
                    />
                  </span>{" "}
                  that's both affordable and effective.
                </p>
                <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow text-center">
                  Our vision is to revolutionize tutoring by connecting students with relatable peer mentors who understand their challenges firsthand. We're not just teaching subjects—we're building confidence, fostering understanding, and creating a community where learning is accessible to all.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Join Us CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <Card className="p-12 md:p-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 dark:bg-gradient-to-br dark:from-orange-600 dark:via-amber-600 dark:to-yellow-600 border-none shadow-3xl text-white text-center relative overflow-hidden group">
            {/* Animated background */}
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ duration: 8, repeat: Infinity }}
              className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/10"
            />

            {/* Floating stars */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                animate={{ 
                  y: [0, -40, 0],
                  rotate: [0, 360, 0],
                  opacity: [0.2, 0.8, 0.2]
                }}
                transition={{ 
                  duration: 5 + i, 
                  repeat: Infinity,
                  delay: i * 0.6
                }}
                className="absolute"
                style={{
                  top: `${15 + i * 10}%`,
                  left: `${5 + i * 12}%`,
                  transform: `scale(${0.5 + Math.random()})`
                }}
              >
                <Star className="w-8 h-8 text-yellow-300 dark:text-amber-300" />
              </motion.div>
            ))}

            <div className="relative">
              <div className="flex items-center justify-center gap-4 mb-10 flex-wrap">
                <motion.div 
                  animate={{ rotate: 360 }} 
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-14 h-14 text-yellow-300 dark:text-amber-300" />
                </motion.div>
                <h3 className="text-5xl md:text-6xl font-black drop-shadow-2xl text-center">
                  Want to Join Our Team?
                </h3>
                <motion.div 
                  animate={{ rotate: -360 }} 
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-14 h-14 text-yellow-300 dark:text-amber-300" />
                </motion.div>
              </div>
              <p className="text-2xl text-white/95 mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                We're always looking for passionate tutors who want to make a difference in students' lives.
              </p>
              <motion.a
                href="/apply"
                className="inline-block bg-white/95 dark:bg-zinc-900/95 text-purple-600 dark:text-orange-400 px-14 py-6 rounded-3xl font-black text-xl shadow-3xl hover:shadow-4xl transition-all duration-300 backdrop-blur-sm border-2 border-white/30 dark:border-orange-500/30 relative overflow-hidden group"
                whileHover={{ scale: 1.08, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                Apply to Become a Tutor
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </motion.a>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}